// utils/analytics.js
class AnalyticsTracker {
  constructor() {
    this.config = {
      apiUrl: process.env.REACT_APP_ANALYTICS_API_URL || 'https://insightdata-27a8ba1af1b4.herokuapp.com/api/analytics',
      appName: 'BridgenApp',
      appVersion: '1.0.0',
      debug: process.env.NODE_ENV === 'development',
      batchSize: 10,
      flushInterval: 5000, // 5 seconds
      maxQueueSize: 100
    };

    this.queue = [];
    this.isProcessing = false;
    this.sessionId = this.getOrCreateSessionId();
    this.anonymousId = this.getOrCreateAnonymousId();
    this.userId = null;
    this.userInfo = {};
    this.pageStartTime = Date.now();
    
    this.init();
  }
  // Get or create session ID
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  // Get or create anonymous ID
  getOrCreateAnonymousId() {
    let anonymousId = localStorage.getItem('analytics_anonymous_id');
    if (!anonymousId) {
      anonymousId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('analytics_anonymous_id', anonymousId);
    }
    return anonymousId;
  }

  // Set user info when logged in
  setUser(user) {
    if (user) {
      this.userId = user._id;
      this.userInfo = {
        userId: user._id,
        userEmail: user.email,
        userName: user.name
      };
      
      // Track login event
      this.track('login', {
        userEmail: user.email,
        userName: user.name
      });
    }
  }

  // Clear user info when logged out
  clearUser() {
    if (this.userId) {
      this.track('logout', {
        userId: this.userId
      });
    }
    
    this.userId = null;
    this.userInfo = {};
  }

  // Initialize tracker
  init() {
    // Auto-flush interval
    setInterval(() => this.flushQueue(), this.config.flushInterval);
    
    // Flush before page unload
    window.addEventListener('beforeunload', () => {
      if (navigator.sendBeacon && this.queue.length > 0) {
        const data = JSON.stringify({
          interactions: this.queue,
          sessionId: this.sessionId,
          anonymousId: this.anonymousId,
          ...this.userInfo
        });
        navigator.sendBeacon(`${this.config.apiUrl}/track/bulk`, data);
      } else {
        this.flushQueueSync();
      }
    });

    // Track session start
    this.track('session_start', {
      referrer: document.referrer,
      landingPage: window.location.href
    });
  }

  // Track generic event
  track(eventType, properties = {}) {
    const event = {
      interactionType: eventType,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pageTitle: document.title,
      pagePath: window.location.pathname,
      pageQuery: Object.fromEntries(new URLSearchParams(window.location.search)),
      ...properties,
      ...this.getDeviceInfo(),
      metadata: {
        appName: this.config.appName,
        appVersion: this.config.appVersion,
        ...(properties.metadata || {})
      }
    };

    this.queue.push(event);

    // Flush if queue reaches max size
    if (this.queue.length >= this.config.batchSize) {
      this.flushQueue();
    }

    return event;
  }

  // Convenience methods
  trackPageView(pageName = null) {
    const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
    this.pageStartTime = Date.now();
    
    return this.track('page_view', {
      pageName: pageName || document.title,
      pageReferrer: document.referrer,
      duration
    });
  }

  trackProductView(product) {
    return this.track('product_view', {
      productId: product._id,
      productName: product.name,
      productPrice: product.price,
      productCurrency: product.currency,
      productCategory: product._type,
      pagePath: `/category/${product.slug?.current}`,
      ...(product.location && { location: product.location })
    });
  }

  trackCategoryView(category) {
    return this.track('category_view', {
      categoryId: category._id,
      categoryName: category.name,
      pagePath: `/items/${category.slug?.current}`
    });
  }

  trackAddToCart(product, quantity = 1) {
    return this.track('add_to_cart', {
      productId: product._id,
      productName: product.name,
      productPrice: product.price,
      productCurrency: product.currency,
      productCategory: product._type,
      quantity,
      pagePath: '/cart'
    });
  }

  trackRemoveFromCart(product, quantity = 1) {
    return this.track('remove_from_cart', {
      productId: product._id,
      productName: product.name,
      productPrice: product.price,
      quantity,
      pagePath: '/cart'
    });
  }

  trackPurchase(order) {
    return this.track('purchase', {
      orderId: order._id,
      orderAmount: order.total,
      orderCurrency: order.currency,
      orderItems: order.items?.map(item => ({
        productId: item.productId,
        productName: item.name,
        quantity: item.quantity,
        price: item.price,
        location:item.location
      })),
      pagePath: '/checkout'
    });
  }

  trackSearch(query, resultsCount = 0, filters = {}) {
    return this.track('search', {
      searchQuery: query,
      searchResults: resultsCount,
      searchFilters: filters,
      pagePath: '/search'
    });
  }

  trackWishlistAdd(product) {
    return this.track('wishlist_add', {
      productId: product._id,
      productName: product.name,
      productPrice: product.price,
      productCategory: product._type
    });
  }

  trackWishlistRemove(product) {
    return this.track('wishlist_remove', {
      productId: product._id,
      productName: product.name
    });
  }

  trackPWAInstall(status) {
    return this.track('pwa_install', {
      pwaInstallStatus: status,
      platform: 'pwa'
    });
  }

  trackPWALaunch() {
    return this.track('pwa_launch', {
      platform: 'pwa',
      launchType: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser'
    });
  }

  trackSubscriptionView(planType) {
    return this.track('subscription_view', {
      subscriptionAction: 'view',
      subscriptionType: planType,
      pagePath: '/subscription'
    });
  }

  trackSubscriptionPurchase(planType, amount) {
    return this.track('subscription_view', {
      subscriptionAction: 'subscribe',
      subscriptionType: planType,
      subscriptionAmount: amount,
      pagePath: '/subscription'
    });
  }

  trackTrialStart(planType) {
    return this.track('subscription_view', {
      subscriptionAction: 'trial_start',
      subscriptionType: planType,
      pagePath: '/subscription'
    });
  }

  trackError(error, context = {}) {
    return this.track('error', {
      errorMessage: error.message,
      errorStack: error.stack,
      errorCode: error.code,
      ...context
    });
  }

  trackHeartbeat(duration = 0) {
    return this.track('heartbeat', {
      duration
    });
  }

  trackClick(element, context = {}) {
    return this.track('click', {
      element,
      ...context
    });
  }

  trackScroll(depth) {
    return this.track('scroll', {
      scrollDepth: depth
    });
  }

  trackTimeSpent(duration) {
    return this.track('time_spent', {
      duration
    });
  }

  // Get device info
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    
    return {
      userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: this.getPlatform(),
      deviceType: this.getDeviceType(),
      deviceOS: this.getDeviceOS(userAgent),
      deviceBrowser: this.getBrowser(userAgent)
    };
  }

  getPlatform() {
    if (navigator.userAgent.toLowerCase().includes('wv')) return 'twa';
    if (window.matchMedia('(display-mode: standalone)').matches) return 'pwa';
    return 'web';
  }

  getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(userAgent)) return 'mobile';
    if (/tablet|ipad/.test(userAgent)) return 'tablet';
    return 'desktop';
  }

  getDeviceOS(userAgent) {
    if (/android/.test(userAgent)) return 'android';
    if (/ios|iphone|ipad/.test(userAgent)) return 'ios';
    if (/windows/.test(userAgent)) return 'windows';
    if (/mac/.test(userAgent)) return 'macos';
    if (/linux/.test(userAgent)) return 'linux';
    return 'unknown';
  }

  getBrowser(userAgent) {
    if (/chrome/.test(userAgent) && !/edg/.test(userAgent)) return 'chrome';
    if (/firefox/.test(userAgent)) return 'firefox';
    if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) return 'safari';
    if (/edg/.test(userAgent)) return 'edge';
    return 'unknown';
  }

  // Queue management
  async flushQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    const batch = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(`${this.config.apiUrl}/track/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': this.sessionId,
          'X-Anonymous-Id': this.anonymousId,
          ...(this.userId && { 'X-User-Id': this.userId }),
          ...(this.userInfo.userEmail && { 'X-User-Email': this.userInfo.userEmail }),
          ...(this.userInfo.userName && { 'X-User-Name': this.userInfo.userName })
        },
        body: JSON.stringify({
          interactions: batch,
          apiKey: process.env.REACT_APP_ANALYTICS_API_KEY
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to flush queue:', error);
      // Re-add to queue for retry
      this.queue = [...batch, ...this.queue];
    } finally {
      this.isProcessing = false;
    }
  }

  flushQueueSync() {
    if (this.queue.length === 0) return;

    const batch = [...this.queue];
    this.queue = [];

    try {
      fetch(`${this.config.apiUrl}/track/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': this.sessionId,
          'X-Anonymous-Id': this.anonymousId,
          ...(this.userId && { 'X-User-Id': this.userId })
        },
        body: JSON.stringify({ interactions: batch }),
        keepalive: true
      });
    } catch (error) {
      console.error('Failed to sync flush analytics:', error);
    }
  }

  // Reset session (for testing)
  resetSession() {
    sessionStorage.removeItem('analytics_session_id');
    this.sessionId = this.getOrCreateSessionId();
    this.queue = [];
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

export default analytics;